import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fetchData } from '../../helpers/apiUtils';

/**
 * Werbliche Strecke der Wiederholung (01.10.2026, Doku .claude/docs/webinar-rerun-system.md § Werbliche Strecke):
 * je Quelle mit geplanter Sendung die KI-Texte der Ein-Klick-Anmeldeseite und der Mails an Angemeldete
 * (Bestätigung, Erinnerung 2 h vorher, Erinnerung zum Start). Automatisch erzeugt und geprüft; gesperrt oder
 * gescheitert = die neutrale Fassung läuft weiter. Nur ansehen und „Neu generieren", kein Bearbeiten.
 */

const API = '/api/webinar-rerun-admin';

type CopyStatus = 'pending' | 'ok' | 'blocked' | 'error';

interface Finding {
  art: string;
  feld: string;
  zitat: string;
  sperrt: boolean;
}

interface MailVorschau {
  key: 'bestaetigung' | 'erinnerung_d0' | 'erinnerung_t5';
  subject: string;
  preview_text: string;
  html: string;
}

interface PromoCopy {
  id: number;
  status: CopyStatus;
  findings: Finding[];
  model: string | null;
  attempts: number;
  last_error: string | null;
  updated_at: string;
  vorschau: unknown;
}

interface PromoSource {
  webinar_id: string;
  title: string;
  slots: { slot: 'anmeldeseite' | 'mails'; copy: PromoCopy | null }[];
}

const MAIL_LABEL: Record<MailVorschau['key'], string> = {
  bestaetigung: 'Bestätigung (sofort, hier „morgen")',
  erinnerung_d0: 'Erinnerung 2 h vorher',
  erinnerung_t5: 'Erinnerung zum Start',
};

function chip(copy: PromoCopy | null): { label: string; color: 'success' | 'error' | 'warning' | 'default' } {
  if (!copy) return { label: 'noch nicht erzeugt', color: 'default' };
  if (copy.status === 'ok') return { label: 'frei', color: 'success' };
  if (copy.status === 'blocked') return { label: 'gesperrt — neutral', color: 'error' };
  if (copy.status === 'error') return { label: 'KI gescheitert — neutral', color: 'error' };
  return { label: 'in Arbeit', color: 'warning' };
}

function formatBerlin(s: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(String(s || ''));
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : '—';
}

const RerunPromoCopiesSection: React.FC = () => {
  const [sources, setSources] = useState<PromoSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; severity: 'info' | 'error' } | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const r = await fetchData<PromoSource[]>(`${API}/promo-copies`);
    if (!aliveRef.current) return;
    if (!r.success || !r.data) {
      setError(r.error || 'Werbliche Texte konnten nicht geladen werden');
      return;
    }
    setError(null);
    setSources(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const regenerate = async (id: number) => {
    setBusy(id);
    setMessage(null);
    const r = await fetchData(`${API}/copies/${id}/regenerate`, { method: 'POST' });
    if (!aliveRef.current) return;
    setBusy(null);
    setMessage(
      r.success
        ? { text: 'Neu-Generierung angestoßen — der nächste Lauf (alle 15 Minuten) schreibt neu. Bis dahin läuft die neutrale Fassung.', severity: 'info' }
        : { text: r.error || 'Neu-Generierung fehlgeschlagen', severity: 'error' },
    );
    await load();
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Werbliche Strecke: Anmeldeseite und Mails an Angemeldete</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => load()}>
          Aktualisieren
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Für jede Quelle mit geplanter Sendung schreibt die KI die Texte der Ein-Klick-Anmeldeseite (Ziel der Einladungen) und
        der Mails an Angemeldete. Eine feste Prüfung sperrt Live-, Termin-, Knappheits- und erfundene Zahlenangaben. Werblich geht
        nur an Anmeldungen über eine Einladung (Welcome, Nachfass, Test) mit aktueller Werbe-Einwilligung — alle anderen und jeder
        gesperrte Text bekommen die neutrale Fassung.
      </Typography>
      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!sources && !error && <CircularProgress size={22} />}
      {sources && sources.length === 0 && <Typography color="text.secondary">Keine geplante Sendung ab gestern.</Typography>}
      {sources?.map((src) => (
        <Box key={src.webinar_id} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {src.title} <Typography component="span" color="text.secondary">({src.webinar_id})</Typography>
          </Typography>
          {src.slots.map(({ slot, copy }) => {
            const c = chip(copy);
            const sperrend = (copy?.findings || []).filter((f) => f.sperrt);
            const mails = slot === 'mails' && copy?.vorschau ? (copy.vorschau as { absender: string; mails: MailVorschau[] }) : null;
            const lp = slot === 'anmeldeseite' && copy?.vorschau ? (copy.vorschau as { headline: string; subheadline: string; button: string }) : null;
            return (
              <Paper key={slot} variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 600 }}>{slot === 'anmeldeseite' ? 'Anmeldeseite' : 'Mails an Angemeldete'}</Typography>
                  <Chip size="small" label={c.label} color={c.color} />
                  {copy && (
                    <Typography variant="caption" color="text.secondary">
                      {formatBerlin(copy.updated_at)} · {copy.model || '—'} · Versuche {copy.attempts}
                    </Typography>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  {copy && slot === 'anmeldeseite' && copy.status === 'ok' && (
                    <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => window.open(`${API}/promo-copies/${copy.id}/lp-preview`, '_blank', 'noopener')}>
                      Vorschau öffnen
                    </Button>
                  )}
                  {copy && (
                    <Button size="small" disabled={busy === copy.id} onClick={() => regenerate(copy.id)}>
                      Neu generieren
                    </Button>
                  )}
                </Stack>
                {copy?.last_error && (
                  <Typography variant="caption" color="error" component="div">
                    Letzter Fehler: {copy.last_error}
                  </Typography>
                )}
                {sperrend.length > 0 && (
                  <Box component="ul" sx={{ m: '6px 0 0', pl: 2.5 }}>
                    {sperrend.slice(0, 8).map((f, i) => (
                      <li key={i}>
                        <Typography variant="caption">
                          {f.art} ({f.feld}): „{f.zitat}“
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
                {lp && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{lp.headline}</Typography>
                    <Typography variant="body2" color="text.secondary">{lp.subheadline}</Typography>
                    <Typography variant="caption">Knopf: {lp.button}</Typography>
                  </Box>
                )}
                {mails && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Absender: {mails.absender}
                    </Typography>
                    {mails.mails.map((m) => {
                      const key = `${copy?.id}-${m.key}`;
                      return (
                        <Box key={m.key} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {MAIL_LABEL[m.key]}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.subject}</Typography>
                          <Typography variant="body2" color="text.secondary">{m.preview_text}</Typography>
                          <Button size="small" onClick={() => setOffen(offen === key ? null : key)}>
                            {offen === key ? 'Text ausblenden' : 'Text zeigen'}
                          </Button>
                          {offen === key && (
                            <Box
                              component="iframe"
                              title={MAIL_LABEL[m.key]}
                              sandbox=""
                              srcDoc={`<div style="font-family:Arial,sans-serif;font-size:15px;line-height:1.5;max-width:600px">${m.html}</div>`}
                              sx={{ width: '100%', height: 420, border: '1px solid #ddd', borderRadius: 1, mt: 0.5 }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      ))}
    </Paper>
  );
};

export default RerunPromoCopiesSection;
